
export default function ServiceDetails() {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="details" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row items-start gap-12 mb-16">
          <div className="lg:w-1/3">
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Soluciones Integrales de Cuidado
            </h3>
          </div>
          <div className="lg:w-2/3">
            <p className="text-lg text-gray-600 leading-relaxed">
              En URI, entendemos que cada paciente tiene necesidades únicas. Nuestros servicios integrales de asistencia de salud están diseñados para proporcionar cuidado profesional y humanizado ya sea en casa o en instalaciones médicas. Nuestros cuidadores certificados están capacitados para brindar el más alto estándar de cuidado mientras mantienen la dignidad y el respeto por todos los pacientes.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Residential Care Details */}
          <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="relative w-full h-64">
              <img 
                src="https://public.readdy.ai/ai/img_res/2db56008ede34d052e29be541eaf9baa.jpg"
                alt="Servicios profesionales de cuidado residencial en casa"
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute top-4 left-4 px-4 py-1.5 bg-uri-green text-white text-xs font-semibold rounded-full whitespace-nowrap">
                Servicios en Casa
              </span>
            </div>
            <div className="p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Excelencia en Cuidado Residencial</h4>
              <p className="text-sm text-gray-600 mb-4">
                Cuidado personalizado en casa que ayuda a sus seres queridos a mantener independencia mientras reciben apoyo médico profesional.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <i className="ri-heart-pulse-line text-uri-green mt-0.5"></i>
                  <span className="text-sm text-gray-700">Planes de cuidado personalizados 24/7</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-nurse-line text-uri-green mt-0.5"></i>
                  <span className="text-sm text-gray-700">Profesionales de enfermería certificados</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-wheelchair-line text-uri-green mt-0.5"></i>
                  <span className="text-sm text-gray-700">Apoyo de movilidad y rehabilitación</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-medicine-bottle-line text-uri-green mt-0.5"></i>
                  <span className="text-sm text-gray-700">Manejo de medicamentos</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-tools-line text-uri-green mt-0.5"></i>
                  <span className="text-sm text-gray-700">Alquiler de equipos especializados: camas clínicas, concentradores de oxígeno, parales, nebulizadores y más</span>
                </li>
              </ul>

              <button 
                onClick={() => scrollToSection('intake')}
                className="w-full py-2.5 bg-uri-green text-white text-sm font-semibold rounded-lg hover:bg-uri-green-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar Información
              </button>
            </div>
          </article>

          {/* Public Hospital Support */}
          <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="relative w-full h-44">
              <img 
                src="https://static.readdy.ai/image/f384832b5a766ad07a1721f0b33de73e/f4e678326dae8ead2ba588345fa50535.jpeg"
                alt="Servicios de asistencia hospitalaria en instalaciones públicas"
                className="w-full h-full object-cover object-top"
              />
              <span className="absolute top-4 left-4 px-4 py-1.5 bg-uri-blue text-white text-xs font-semibold rounded-full whitespace-nowrap">
                Instalaciones Públicas
              </span>
            </div>
            <div className="p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Apoyo en Hospitales Públicos</h4>
              <p className="text-sm text-gray-600 mb-4">
                Acompañamiento profesional y apoyo para pacientes en áreas de emergencia y cuidados intensivos.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <i className="ri-first-aid-kit-line text-uri-blue mt-0.5"></i>
                  <span className="text-sm text-gray-700">Asistencia en emergencias</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-heart-3-line text-uri-blue mt-0.5"></i>
                  <span className="text-sm text-gray-700">Cuidados intensivos adulto y pediátrico</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-team-line text-uri-blue mt-0.5"></i>
                  <span className="text-sm text-gray-700">Coordinación con personal médico</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-file-list-3-line text-uri-blue mt-0.5"></i>
                  <span className="text-sm text-gray-700">Asistencia con documentación</span>
                </li>
              </ul>

              <button 
                onClick={() => scrollToSection('booking')}
                className="w-full py-2.5 bg-uri-blue text-white text-sm font-semibold rounded-lg hover:bg-uri-blue-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Verificar Disponibilidad
              </button>
            </div>
          </article>

          {/* Private Center Care */}
          <article className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
            <div className="relative w-full h-64">
              <img 
                src="https://static.readdy.ai/image/f384832b5a766ad07a1721f0b33de73e/504849741681eb5a8a66083cc6f12c39.png"
                alt="Servicios de cuidado premium en centros médicos privados"
                className="w-full h-full object-cover object-center"
              />
              <span className="absolute top-4 left-4 px-4 py-1.5 bg-uri-teal text-white text-xs font-semibold rounded-full whitespace-nowrap">
                Centros Privados
              </span>
            </div>
            <div className="p-6">
              <h4 className="text-xl font-bold text-gray-900 mb-3">Excelencia en Centros Privados</h4>
              <p className="text-sm text-gray-600 mb-4">
                Servicios de asistencia premium en clínicas privadas incluyendo emergencias y UCI.
              </p>
              
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <i className="ri-first-aid-kit-line text-uri-teal mt-0.5"></i>
                  <span className="text-sm text-gray-700">Asistencia en emergencias</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-heart-3-line text-uri-teal mt-0.5"></i>
                  <span className="text-sm text-gray-700">UCI adulto y pediátrico</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-vip-crown-line text-uri-teal mt-0.5"></i>
                  <span className="text-sm text-gray-700">Coordinación de cuidado premium</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-phone-line text-uri-teal mt-0.5"></i>
                  <span className="text-sm text-gray-700">Línea de comunicación directa</span>
                </li>
              </ul>

              <button 
                onClick={() => scrollToSection('intake')}
                className="w-full py-2.5 bg-uri-teal text-white text-sm font-semibold rounded-lg hover:bg-uri-teal-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Solicitar Información
              </button>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}
